import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { from, Observable, of, tap } from 'rxjs';
import { RootState } from '..';
import { AzureMonitoringService } from '../../services/AzureMonitoringService';
import { FlowRestService } from '../../services/rest/flow.rest.service';
import { util } from '../../util/util';
import { AbstractEffects } from '../AbstractEffects';
import { isCombinationMaterial, isPackingMaterial, Material, } from '../edit-florder-detail/florder-detail.interface';
import { Florder } from '../../interfaces/florder.interface';
import * as actions from './flow-detail.actions';
import {
  FlowAcceptFlowSuccessAction,
  FlowDetailCheckFlowDateAction,
  FlowDetailDocumentDownloadAction,
  FlowDetailRequestDeliveryDatesAction,
  FlowDetailSaveFlowDetailAction
} from './flow-detail.actions';
import { catchError, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationService } from 'app/core/services/notification.service';

@Injectable({ providedIn: 'root' })
export class FlowDetailEffects extends AbstractEffects {

  triggerRequestDeliveryDatesWhenSavingFlowDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<FlowDetailSaveFlowDetailAction>(actions.FLOWDETAIL_CREATE_SAVE_FLOW_DETAIL),
      withLatestFrom(this.store, (action, state) => {
        return {
          action,
          state,
        };
      }),
      switchMap(({ action, state }) => {
        const newActions = [];
        if (!state.editFlowDetail.mode.createTemplate && !state.editFlowDetail.mode.editTemplate && state.editFlowDetail.mode.state !== 'edit-confirm') {
          newActions.push(new actions.FlowDetailRequestDeliveryDatesAction(new actions.FlowDetailSaveFlowDetailGetDatesSuccessAction()));
        } else {
          newActions.push(new actions.FlowDetailSaveFlowDetailGetDatesSuccessAction());
        }
        return newActions;
      })
    )
  );
  // FlowDetailCreateSetDeliveryMethod
  requestDeliveryDates$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<FlowDetailRequestDeliveryDatesAction>(actions.FLOWDETAIL_REQUEST_DELIVERY_DATES),
      debounceTime(50),
      withLatestFrom(this.store, (action: FlowDetailRequestDeliveryDatesAction, state) => {
        return {
          state,
          type: action.type,
          payload: action.payload,
        };
      }),
      switchMap(({ type, payload, state }) => {
        return this.flowRestService.getDeliveryDates(state.editFlowDetail.flowDetail.deliveryMethod).pipe(
          switchMap(result => {
            if (payload) {
              return from([new actions.FlowDetailRequestDeliveryDatesSuccessAction(result), payload]);
            } else {
              return from([new actions.FlowDetailRequestDeliveryDatesSuccessAction(result)]);
            }
          }),
          catchError(err => this.handleFail(err, 'ORDERS.DETAIL.LABELS.GET_DELIVERY_DATES', [of(new actions.FlowDetailRequestDeliveryDatesFailAction(err))]))
        );
      })
    )
  );

  // ORDERDETAIL_CREATE_START
  downloadDocument$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<FlowDetailDocumentDownloadAction>(actions.FLOWDETAIL_DOCUMENT_DOWNLOAD),
      debounceTime(50),
      switchMap((action: FlowDetailDocumentDownloadAction) =>
        this.flowRestService.getFlowDocument(action.payload.etmOrderNumber, action.payload.customerIds).pipe(
          map((blob: Blob) => {
            return new actions.FlowDetailDocumentDownloadSuccessAction({
              filename: `flow-${action.payload.etmOrderNumber}.${util.getExtension(blob.type)}`,
              blob,
            });
          }),
          catchError(err => this.handleFail(err, `ERRORS.${action.payload.extension?.toUpperCase()}_DOWNLOAD_FAILED.TITLE`, [of(new actions.FlowDetailDocumentDownloadFailAction(err))]))
        )
      )
    )
  );
  checkFlowDate$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<FlowDetailCheckFlowDateAction>(actions.FLOWDETAIL_CHECK_FLOW_DATE),
      debounceTime(50),
      switchMap((action: FlowDetailCheckFlowDateAction) =>
        this.flowRestService.validateFlowDate(action.payload.value, action.payload.frequency, action.payload.fromId, action.payload.toId, action.payload.flowWeek).pipe(
          map((data: any) => new actions.FlowDetailCheckFlowDateSuccessAction(data)),
          catchError(err => [new actions.FlowDetailCheckFlowDateErrorAction(err.error)])
        )
      )
    )
  );
  flowAccept$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.FLOWDETAIL_ACCEPT_FLOW_START),
      withLatestFrom(this.store, (action, state) => {
        return { state };
      }),
      switchMap(({ state }) => {
        const flowDetail = state.editFlowDetail.flowDetail;
        const flow: Florder = {
          id: null,
          flowType: flowDetail.deliveryMethod.type,
          customerRefNumber: flowDetail.deliveryMethod.customerReferenceNumber,
          senderRefNumber: flowDetail.deliveryMethod.senderReferenceNumber,
          salesOrderNumber: null,
          deliveryNumber: null,
          shipmentNumbers: [],
          etmOrderNumber: flowDetail.etmOrderNumber,
          from: flowDetail.deliveryMethod.from,
          to: flowDetail.deliveryMethod.to,
          requestedUnloadingDate: null,
          confirmedUnloadingDate: null,
          shippingCondition: null,
          globalType: flowDetail.globalType,
          status: null,
          transporter: flowDetail.deliveryMethod.transporter,
          clearing: util.toString(flowDetail.deliveryMethod.clearing),
          licensePlate: flowDetail.deliveryMethod.licensePlate,
          comments: flowDetail.deliveryMethod.remarksHandshaker,
          wholesaler: flowDetail.deliveryMethod.wholesaler,

          updateTime: flowDetail.updateTime,
          updateDate: flowDetail.updateDate,
          updateBy: flowDetail.updateBy,
          flowDate: flowDetail.planning.flowDate,

          flowLineItems: flowDetail.materials.map((m: Material) => {
            return {
              itemNumber: m.internalId,
              packingId: (isPackingMaterial(m) || isCombinationMaterial(m)) && m.packingId || undefined,
              packingStatus: m.packingStatus && m.packingStatus,
              originalQuantity: 0, // Is skipped
              definitiveQuantity: (isPackingMaterial(m) || isCombinationMaterial(m)) && util.toNumber(m.packingQuantity) || undefined,
              differenceQuantity: 0, // Is skipped
              contentId: m.contentId,
              lineReferenceId: m.lineReferenceId,
              isNew: m.isNew,
            };
          }),
        };

        return this.flowRestService.acceptFlow(flow).pipe(
          map(() => new actions.FlowAcceptFlowSuccessAction({ flow })),
          catchError(err => {
            return this.handleFail(err, 'FLOWS.NOTIFICATIONS.ACCEPT_FLOW', [of(new actions.FlowAcceptFlowFailAction(util.getErrorMessage(err)))]);
          })
        );
      })
    )
  );

  flowAcceptSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.FLOWDETAIL_ACCEPT_FLOW_SUCCESS),
      withLatestFrom(this.store, (action: FlowAcceptFlowSuccessAction, state) => ({
        ...action,
        flow: action.payload.flow,
        showDownloadDocumentButton: !!state.session.customerPermissions.data.queryOrGetFlow,
        customerIds: state.session.customerPermissions.data.queryOrGetFlow?.map(c => c.id),
      })),
      tap(({ flow, showDownloadDocumentButton, customerIds }) => {
        this.notificationService.showSubmitFeedbackDialog({
          isSuccess: true,
          messageType: 'FLOW_ACCEPT',
          route: { backUrl: 'flows', moduleRoute: 'flows' },
          data: {
            etmOrderNumber: flow.etmOrderNumber,
            salesOrderNumber: flow.salesOrderNumber,
          },
          methods: {
            downloadDocument: () => {
              this.store.dispatch(new actions.FlowDetailDocumentDownloadAction({ etmOrderNumber: flow.etmOrderNumber, extension: 'PDF', customerIds }));
            },
          },
          buttons: {
            showDownloadDocumentButton,
          }
        });
      }),
      catchError(({ payload: { flow } }: { payload: { flow: Florder, type: string } }) => {
        this.notificationService.showSubmitFeedbackDialog({
          isSuccess: false,
          messageType: 'FLOW_ACCEPT',
          route: { backUrl: 'flows', moduleRoute: 'flows' },
          data: {
            etmOrderNumber: flow.etmOrderNumber,
            salesOrderNumber: flow.salesOrderNumber,
          }
        });
        return of(null);
      })
    ), { dispatch: false }
  );

  constructor(
    private store: Store<RootState>,
    azureMonitoringService: AzureMonitoringService,
    private actions$: Actions,
    private flowRestService: FlowRestService,
    translateService: TranslateService,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
  }
}
